import { Datasets } from "@/components/ImpactReferenceSelector/Datasets";
import styles from "@/components/ImpactReferenceSelector/ImpactReference.module.scss";
import {
  ImpactReferencePageTabs,
  tabItems,
} from "@/components/ImpactReferenceSelector/ImpactReferencePageTabs";
import useControlledState from "@/hooks/useControlledState";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import { CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useEffect } from "react";
import { EcoInventDatasets } from "./EcoInventDatasets";

export default function ImpactReferenceModal({
  title,
  onClose,
  currentSelectionContent,
  bottomLeft,
  onSubmit,
  isLoading,
  suggestionTabContent,
  selectedDataset,
  onDatasetChange,
  activeTab: parentTab,
  onTabChange: parentTabOnChange,
  defaultDatabaseSearch,
  defaultDatabaseFilter,
  includeExiobase = false,
  defaultTab = tabItems.ecoinvent.key,
}: {
  title: string;
  onClose: () => void;
  currentSelectionContent?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  onSubmit: () => void;
  isLoading: boolean;
  selectedDataset?: ImpactDataset;
  onDatasetChange: (dataset?: ImpactDataset) => void;
  suggestionTabContent?: React.ReactNode;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  defaultDatabaseSearch?: Record<string, any>;
  defaultDatabaseFilter?: Record<string, any>;
  includeExiobase?: boolean;
  defaultTab?: string;
}) {
  const [activeTab, setActiveTab] = useControlledState(defaultTab, parentTab, parentTabOnChange);

  const onCancel = () => {
    onDatasetChange(undefined);
    onClose?.();
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose?.();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const tabContent =
    activeTab === tabItems.suggestions.key ? (
      suggestionTabContent
    ) : activeTab === tabItems.all.key ? (
      <Datasets
        key="All"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        defaultSearch={defaultDatabaseSearch}
        includeExiobase={includeExiobase}
      />
    ) : activeTab === tabItems.ecoinvent.key ? (
      <EcoInventDatasets
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        defaultSearch={defaultDatabaseSearch}
        defaultFilter={defaultDatabaseFilter}
      />
    ) : activeTab === tabItems.agri_footprint.key ? (
      <Datasets
        key="agri_footprint"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        datasetType="agri_footprint"
        defaultSearch={defaultDatabaseSearch}
      />
    ) : activeTab === tabItems.epd.key ? (
      <Datasets
        key="EPD"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        datasetType="EPD"
        defaultSearch={defaultDatabaseSearch}
      />
    ) : activeTab === tabItems.root.key ? (
      <Datasets
        key="root"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        datasetType="ROOT"
        defaultSearch={defaultDatabaseSearch}
      />
    ) : activeTab === tabItems.exiobase.key ? (
      <Datasets
        key="exiobase"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        datasetType="exiobase"
        defaultSearch={defaultDatabaseSearch}
        includeExiobase={true}
      />
    ) : (
      <Datasets
        key="CUSTOM"
        selectedDataset={selectedDataset}
        onDatasetChange={onDatasetChange}
        datasetType="CUSTOM"
        defaultSearch={defaultDatabaseSearch}
      />
    );
  return (
    <div className={styles.MaterialImpactReferenceModal}>
      <div className={styles.header}>
        {title}
        <CloseOutlined
          className={styles.close}
          onClick={onCancel}
        />
      </div>
      <div className={styles.container}>
        {currentSelectionContent && currentSelectionContent}
        <ImpactReferencePageTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showSuggestions={!!suggestionTabContent}
          showExioBase={includeExiobase}
        />
        {tabContent}
      </div>
      <div className={styles.footer}>
        {bottomLeft ? bottomLeft : <span />}
        <div>
          <Button
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            loading={isLoading}
            disabled={!selectedDataset}
            type="primary"
            onClick={onSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
